// Parabon CRM configuration — the domain values that drive the app.
//
// Labels are in French on purpose: they are runtime data shown to the two
// sales users, not code. The `value` keys stay in ASCII kebab-case because
// they are persisted in the database (deals.stage, tasks.type, ...) — never
// rename one without a data migration.
//
// Parabon sells a monthly subscription, so a deal's amount is the MRR the
// customer will pay, not a one-off ticket. See `dealCategories` for the
// plans, kept in sync with ~/Herd/parabon/src/lib/plans.ts.

import type { ConfigurationContextValue } from "@/components/atomic-crm/root/ConfigurationContext";

export const parabonTitle = "Parabon";

export const parabonLightModeLogo = "./logos/parabon_light.svg";
export const parabonDarkModeLogo = "./logos/parabon_dark.svg";

export const parabonCurrency = "EUR";

/** Kind of property the prospect runs — Parabon's actual market. */
export const parabonCompanySectors = [
  { value: "gite", label: "Gîte" },
  { value: "chambres-hotes", label: "Chambres d'hôtes" },
  { value: "meuble-tourisme", label: "Meublé de tourisme" },
  { value: "maison-hotes", label: "Maison d'hôtes" },
  { value: "hebergement-insolite", label: "Hébergement insolite" },
  { value: "domaine-reception", label: "Domaine de réception" },
  { value: "camping", label: "Camping" },
  { value: "hotel", label: "Hôtel" },
  { value: "conciergerie", label: "Conciergerie / gestion locative" },
  { value: "autre", label: "Autre" },
];

/**
 * Sales pipeline. `trial` mirrors the 30-day free plan: a prospect sitting
 * there is already using the product, which is the strongest buying signal
 * we have — it deserves its own column on the Kanban.
 */
export const parabonDealStages = [
  { value: "lead", label: "Lead" },
  { value: "contacted", label: "Contact établi" },
  { value: "demo", label: "Démo faite" },
  { value: "trial", label: "Essai en cours" },
  { value: "proposal", label: "Proposition envoyée" },
  { value: "won", label: "Abonné" },
  { value: "lost", label: "Perdu" },
];

/**
 * Odds that a deal sitting at this stage eventually subscribes, used to weight
 * the forecast on the dashboard chart. Closed stages (`won`, `lost`) are
 * counted at face value and are absent here on purpose. An unknown stage is
 * read as 0 rather than crashing the chart — see DealsChart.
 */
export const parabonStageProbability: Record<string, number> = {
  lead: 0.1,
  contacted: 0.2,
  demo: 0.4,
  trial: 0.6,
  proposal: 0.8,
};

/** Stages excluded from the dashboard pipeline widget: the closed ones. */
export const parabonDealPipelineStatuses = ["won", "lost"];

/** Targeted plan — mirrors PLANS in the Parabon codebase. */
export const parabonDealCategories = [
  { value: "essentiel", label: "Essentiel" },
  { value: "signature", label: "Signature" },
  { value: "excellence", label: "Excellence" },
];

export const parabonNoteStatuses = [
  { value: "cold", label: "Froid", color: "#7dbde8" },
  { value: "warm", label: "Tiède", color: "#e8cb7d" },
  { value: "hot", label: "Chaud", color: "#e88b7d" },
  { value: "in-contract", label: "Abonné", color: "#a4e87d" },
];

export const parabonTaskTypes = [
  { value: "none", label: "Aucun" },
  { value: "call", label: "Appel" },
  { value: "email", label: "Email" },
  { value: "demo", label: "Démo" },
  { value: "follow-up", label: "Relance" },
  { value: "meeting", label: "Rendez-vous" },
  { value: "onboarding", label: "Accompagnement démarrage" },
  { value: "thank-you", label: "Remerciement" },
];

export const parabonConfiguration: ConfigurationContextValue = {
  companySectors: parabonCompanySectors,
  currency: parabonCurrency,
  dealCategories: parabonDealCategories,
  dealPipelineStatuses: parabonDealPipelineStatuses,
  dealStages: parabonDealStages,
  noteStatuses: parabonNoteStatuses,
  taskTypes: parabonTaskTypes,
  title: parabonTitle,
  darkModeLogo: parabonDarkModeLogo,
  lightModeLogo: parabonLightModeLogo,
};
